import * as React from "react";
import {useCallback} from "react";
import {ArchiveIcon, CircleAlertIcon, CloudUpload, DatabaseArrowUp, Loader2Icon} from "lucide-react";
import {useNavigate, useParams} from "react-router";
import {Button, buttonVariants} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Card, CardDescription, CardTitle} from "@/components/ui/card.tsx";
import {BackupCreationProgressEvent, RestoreProgressEvent} from "@/backup/RestoreProgress.ts";
import {RestoreProgressPanel} from "@/backup/RestoreProgressPanel.tsx";
import {BackupList} from "@/backup/BackupList.tsx";
import {BackupDetails} from "@/backup/BackupDetails.tsx";
import {useBackups} from "@/backup/useBackups.ts";
import {useBackupDetail} from "@/backup/useBackupDetail.ts";
import {useRestoreJob} from "@/backup/useRestoreJob.ts";
import {useCreationJob} from "@/backup/useCreationJob.ts";
import {cn} from "@/lib/utils.ts";

export default function BackupView() {
    const {id} = useParams();
    const navigate = useNavigate();
    const {backups, pendingMutation, error: backupError, refresh, upload, remove} = useBackups();
    const {backup, loading: detailLoading, error: detailError, refresh: refreshDetail} = useBackupDetail(id);

    const handleRestoreFinished = useCallback((event: RestoreProgressEvent) => {
        void refresh();
        if (event.backupId === id) void refreshDetail();
    }, [id, refresh, refreshDetail]);

    const {event: restoreProgress, error: restoreError, busy: restoreBusy, start: startRestore} =
        useRestoreJob(handleRestoreFinished);
    const handleCreationStarted = useCallback((event: BackupCreationProgressEvent) => { void refresh(); navigate(`/admin/backups/${event.backupId}`); }, [navigate, refresh]);
    const handleCreationFinished = useCallback((event: BackupCreationProgressEvent) => { void refresh(); if(event.backupId===id) void refreshDetail(); }, [id, refresh, refreshDetail]);
    const {event: creationProgress,error:creationError,busy:creationBusy,start:startCreation}=useCreationJob(handleCreationStarted,handleCreationFinished);
    const selectedRestoreProgress = (creationProgress?.backupId === backup?.id ? creationProgress : null) ?? (restoreProgress?.backupId === backup?.id ? restoreProgress : null);
    const busy = pendingMutation !== null || restoreBusy || creationBusy;

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const file = input.files?.[0];
        if (!file) return;
        await upload(file);
        input.value = "";
    };

    const handleDelete = async () => {
        if (!backup || !await remove(backup.id)) return;
        navigate("/admin/backups", {replace: true});
    };

    const actionError = backupError ?? restoreError ?? creationError;

    return <div className="flex h-full justify-center">
        <title>Backups | DireDawn</title>
        <div className="container max-w-7xl space-y-5 px-4 py-6 md:px-6">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Backups</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Datenbestand sichern, prüfen und vollständig wiederherstellen.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <label htmlFor="backup-upload" title="Backup hochladen" aria-disabled={busy}
                           className={cn(buttonVariants({variant: "outline"}),
                               busy && "pointer-events-none opacity-50")}>
                        {pendingMutation === "upload"
                            ? <>Wird hochgeladen<Loader2Icon className="animate-spin"/></>
                            : <>Backup hochladen<CloudUpload/></>}
                    </label>
                    <Input id="backup-upload" type="file" className="hidden" accept=".zip,application/zip"
                           disabled={busy} onChange={handleUpload}/>
                    <Button title="Backup erstellen" disabled={busy}
                            onClick={() => void startCreation()}>
                        {creationBusy
                            ? <>Wird erstellt<Loader2Icon className="animate-spin"/></>
                            : <>Backup erstellen<DatabaseArrowUp/></>}
                    </Button>
                </div>
            </header>

            {actionError && <div role="alert"
                                 className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                <CircleAlertIcon className="mt-0.5 size-4 shrink-0"/>{actionError}
                {backupError && <Button variant="outline" size="sm" onClick={() => void refresh()}>Erneut laden</Button>}
            </div>}

            <div className="grid items-start gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
                <BackupList backups={backups} selectedId={id}/>

                <div className="min-w-0 space-y-5">
                    {restoreProgress && !selectedRestoreProgress && <RestoreProgressPanel event={restoreProgress}/>}
                    {creationProgress && creationProgress.backupId !== backup?.id && <RestoreProgressPanel event={creationProgress} operation="creation"/>}

                    {detailLoading && <Card className="items-center py-16" role="status">
                        <Loader2Icon className="size-6 animate-spin text-muted-foreground"/>
                        <span className="text-sm text-muted-foreground">Backup wird geladen…</span>
                    </Card>}

                    {id && detailError && <Card className="items-center py-16 text-center">
                        <CircleAlertIcon className="size-8 text-destructive"/>
                        <div>
                            <CardTitle>Backup konnte nicht geladen werden</CardTitle>
                            <CardDescription className="mt-2">Die Verbindung ist möglicherweise vorübergehend unterbrochen.</CardDescription>
                        </div>
                        <Button variant="outline" onClick={() => void refreshDetail()}>Erneut versuchen</Button>
                    </Card>}

                    {id && !detailLoading && !detailError && !backup && <Card className="items-center py-16 text-center">
                        <CircleAlertIcon className="size-8 text-muted-foreground"/>
                        <div>
                            <CardTitle>Backup nicht gefunden</CardTitle>
                            <CardDescription className="mt-2">Zu dieser ID ist kein Backup vorhanden.</CardDescription>
                        </div>
                    </Card>}

                    {!id && !detailLoading && <Card className="items-center py-16 text-center">
                        <ArchiveIcon className="size-9 text-muted-foreground"/>
                        <div>
                            <CardTitle>Backup auswählen</CardTitle>
                            <CardDescription className="mt-2">
                                Wähle links eine Sicherung aus, um Inhalt und Aktionen anzuzeigen.
                            </CardDescription>
                        </div>
                    </Card>}

                    {backup && !detailError && <BackupDetails
                        backup={backup}
                        restoreProgress={selectedRestoreProgress}
                        progressOperation={creationProgress?.backupId === backup.id ? "creation" : "restore"}
                        busy={busy}
                        deleting={pendingMutation === "delete"}
                        onRestore={() => void startRestore(backup.id)}
                        onDelete={() => void handleDelete()}
                    />}
                </div>
            </div>
        </div>
    </div>;
}
